/**
 * A collection of higher level utilities dealing with the global style system.
 */

/* global window */

import {
  Section,
  VENDOR_PROPERTIES_SUPPORTED,
} from '@packages/systems/style/properties';
import _ from 'lodash';
import * as jsdiff from 'diff';
import {
  isCssVariableProperty,
  stripVendorPrefix,
} from '@packages/systems/core/utils/CssStringUtils';
import {cssPropDetailsFromNativeProperty} from '@packages/shared/LegacyStyleSystem/utils/CssPropertyUtils';
import {
  sortCssProperties,
  type CssPropertyDetail,
} from '@packages/systems/style-legacy/css-properties';
import {cleanCssValue} from './CssFormatUtils';
import {
  toWebflowCssValueObject,
  isMultiPartProp,
  getSubvalueKey,
  StylesMap,
} from '@packages/systems/style-legacy/utils';
import {
  DEFAULT_STYLE_RULE,
  LOCAL_STYLE_RULE,
  type StyleRule,
} from '@packages/systems/style-legacy/rules';
import {StylePath} from '@packages/systems/style-legacy/values';
import {hasValue} from '@packages/systems/core/utils/JsUtils';
import {strLeft, camelize, dasherize} from '@packages/utilities/string-utils';
import {
  constant,
  emptyArray,
  emptyObject,
  identity,
} from '@packages/utilities/fp/utils';
import {assoc} from '@packages/utilities/key-map';

import {Err, Ok, type Result} from '@packages/utilities/fp/result';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {defer, Lazy} from '@packages/utilities/lazy';

import type {Map as ImmutableMap} from 'immutable';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {
  NormalizedAtomConfig,
  LockedStyles,
  ParamsType,
  Component,
  Expressions,
  Plugins,
  matchExpression,
} from '@packages/systems/wfdl';
import type {
  Display,
  WebflowCssProperties,
  WebflowCssProperty,
} from '@packages/systems/core/utils/WebflowCssSubset';
import type {BreakpointID} from '@packages/systems/style/types';
import {PSEUDO_KEYS} from '@packages/systems/core/utils/StyleUtils';
import type {
  PseudoStateKey,
  CssVariablesStateType,
} from '@packages/systems/core/utils/StyleTypes';
import {
  createStyleRuleGuidFromStyleRule,
  getLinkedRules,
} from './StyleRuleUtils';
import {
  createSwatchMap,
  replaceSwatchPlaceholdersForExport,
} from '@packages/systems/style-legacy/swatches';
import {
  UPSTREAM,
  LOCAL,
  DOWNSTREAM,
  DEFAULT,
  CASCADE_CHAIN,
  ID,
} from '@packages/shared/LegacyStyleSystem/constants/StreamTypes';

import {resetValueUpdate, type StyleUpdate} from '../stores/updateStyle';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import * as ElementTypeUtils from '@packages/utilities/elementTypeUtils';
import {
  expandUserComponents,
  getBody,
  getChildren,
  getCurrentComponentRender,
  isComponent,
  isUserComponentRoot,
} from '@packages/utilities/platform';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';

import {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {
  getExpandedBody,
  getLiveComponentMap,
} from '@packages/utilities/platform-selectors';
import {StyleBlockId} from '@packages/systems/style-legacy/records';
import {calculateAffectedForStyleBlock} from '@packages/shared/utils/AffectedNodesUtils';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import {createSelector} from 'reselect';

type StyleMap = ImmutableMap<WebflowCssProperty, string>;

const categorizeRules = (
  rules: Array<StyleRule>,
  currentRule: StyleRule | null | undefined,
  mediaQuery: BreakpointID,
  pseudoMode: PseudoStateKey | null,
  variantState: VariantStateKey | null
) => {
  const currentRuleId = createStyleRuleGuidFromStyleRule(currentRule);
  const currentRuleIndex = currentRuleId
    ? rules.findIndex(
        (rule) => createStyleRuleGuidFromStyleRule(rule) === currentRuleId
      )
    : -1;

  return rules.reduce(
    (categories, rule, index) => {
      if (rule.type === DEFAULT_STYLE_RULE) {
        categories[UPSTREAM].push(rule); // TODO: This is temporarily UPSTREAM but should be DEFAULT once the style panel allows the style system to tell it where its values are coming from.
      } else if (!currentRule && rule.type !== LOCAL_STYLE_RULE) {
        categories[UPSTREAM].push(rule);
      } else if (
        rule.type === LOCAL_STYLE_RULE &&
        !rule.inherited &&
        rule.mediaQuery === mediaQuery &&
        rule.pseudoState === pseudoMode
      ) {
        // If we are in a variant, we want the style rules
        // from the base variant to be treated as inherited.
        const bucket = rule.variantState === variantState ? ID : UPSTREAM;
        categories[bucket].push(rule);
      } else if (currentRuleIndex === -1) {
        categories[UPSTREAM].push(rule);
      } else if (index < currentRuleIndex) {
        categories[UPSTREAM].push(rule);
      } else if (index > currentRuleIndex) {
        categories[DOWNSTREAM].push(rule);
      } else {
        categories[LOCAL].push(rule);
      }

      return categories;
    },
    {
      [DEFAULT]: [] as StyleRule[],
      [UPSTREAM]: [] as StyleRule[],
      [DOWNSTREAM]: [] as StyleRule[],
      [LOCAL]: [] as StyleRule[],
      [ID]: [] as StyleRule[],
    }
  );
};

// Limit styles only to those that appear in the supported style map
function shouldExcludeStyleProperty(
  prop: string,
  computedStyles: Partial<Record<WebflowCssProperty, string>>
): boolean {
  // Keep custom properties so we can read/overwrite CSS variables
  if (isCssVariableProperty(prop)) return false;

  // Ignore multipart properties since it will be handled by `subProps` below in buildStyleMap
  if (prop === 'transition' || prop === 'background') return true;

  const isPrefix = prop[0] === '-';
  let name = prop;
  let details;
  if (isPrefix) {
    name = stripVendorPrefix(prop) as WebflowCssProperty;
    details = cssPropDetailsFromNativeProperty(name);

    if (!hasValue(details)) {
      return true;
    }

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<WebflowCssProperty, string>>'.
    if (computedStyles[name] && !VENDOR_PROPERTIES_SUPPORTED.has(prop))
      return true;
  } else {
    details = cssPropDetailsFromNativeProperty(name);
    return !hasValue(details);
  }

  return false;
}

// Map the computed style props to our StyleSystem structure with
// data augmented from the CSS prop table
function mapStyleProp(
  cssProperty: string,
  cssValue: string,
  isEmpty: boolean
): CssPropertyDetail {
  cssProperty = stripVendorPrefix(cssProperty);

  const details = cssPropDetailsFromNativeProperty(cssProperty) || emptyObject;

  // Ignore padding added by wf-empty
  if (isEmpty && cssProperty.indexOf('padding') === 0) {
    cssValue = '0px';
  }
  // If the value is still prefixed, remove the prefix

  const result = {
    prop: cssProperty as WebflowCssProperty,
    value: {},
    from: {},
    path: details.path,
    section: details.section,
    typographic: details.typographic,
    combo: details.combo,
    transforms: details.transforms,
  } as const;

  if (cssValue !== null) {
    // Clean up values to be in Webflow format
    cssValue = cleanCssValue(cssProperty, cssValue, details);

    // Conver to an object (e.g. { v: 'auto' })
    // @ts-expect-error - TS2339 - Property 'browser' does not exist on type '{}'.
    result.value.browser = toWebflowCssValueObject(
      cssValue,
      Boolean(details.combo)
    );
  }

  return result;
}

const {CSS} = window;
const browserSupportsBackdropFilter =
  !(CSS && CSS.supports) || // default to true for non-browser environments
  CSS.supports('backdrop-filter: none') ||
  CSS.supports('-webkit-backdrop-filter: none');

type WebflowCssPropOrVar = WebflowCssProperty | `--${string}`;

/**
 * Computes a map of styles for the current element based on the CSS rules that are applied to it.
 * @param  {Object}           computedStyles    A map of computed styles as read from the browser.
 * @param  {Boolean}          isEmpty           Does the node have the empty decoration applied?
 * @param  {StyleRule[]}      upstreamRules     The list of rules that this element inherits styles from.
 * @param  {StyleRule}        currentRule       The currently selected style rule.
 * @param  {StyleRule[]}      downstreamRules   The list of rules the current rule cascades values to.
 * @param  {String}           mediaQuery        Name of the current media query - used to define what local style rule is current
 * @param  {PSEUDOS}          pseudoMode        Current pseudo mode - used to define what local style rule is current
 * @return {{[WebflowCssProperty]: CssPropertyDetail}}                             A map of styles.
 */
export function buildStyleMap(
  computedStyles: Partial<Record<WebflowCssProperty, string>>,
  isEmpty: boolean,
  rules: StyleRule[],
  currentRule: StyleRule | null | undefined,
  mediaQuery: BreakpointID,
  pseudoMode: PseudoStateKey | null,
  variantState: VariantStateKey | null,
  variables: StyleVariables.VariableMap,
  variableCollections: StyleVariables.VariableCollectionMap,
  slugMap: Plugins.PluginsType['slugMap']
): Partial<Record<WebflowCssProperty, CssPropertyDetail>> {
  // Collect all computed styles into a structured list of objects
  const allStyles = _.reduce<
    Partial<Record<WebflowCssProperty, string>>,
    CssPropertyDetail[]
  >(
    computedStyles,
    (acc, cssValue, cssProperty) => {
      if (shouldExcludeStyleProperty(cssProperty, computedStyles)) {
        return acc;
      }
      acc.push(mapStyleProp(cssProperty, cssValue!, isEmpty));
      return acc;
    },
    []
  );

  // Sort by logical group order
  sortCssProperties(
    allStyles,
    'prop',
    // We can't detect raw values here so we explicitly opt out of sorting raw
    // values to the end:
    null
  );

  // Create a map that makes it easy to access each CSS style by its property name
  // TODO use [].reduce instead to avoid casting.
  const allStylesMap: Partial<Record<WebflowCssPropOrVar, CssPropertyDetail>> =
    _.keyBy(allStyles, 'prop') as any;

  // Walk through each upstream, local, and downstream rule, and through each of their respective CSS property definitions
  // and record the value. If multiple values are defined for the same CSS prop in the same bucket, the last one always wins
  // because the selectors are ordered by specificity
  const categorized = categorizeRules(
    rules,
    currentRule,
    mediaQuery,
    pseudoMode,
    variantState
  );

  const buckets = [
    {mode: DEFAULT, rules: categorized[DEFAULT]},
    {mode: UPSTREAM, rules: categorized[UPSTREAM]},
    {mode: LOCAL, rules: categorized[LOCAL]},
    {mode: DOWNSTREAM, rules: categorized[DOWNSTREAM]},
    {mode: ID, rules: categorized[ID]},
  ];

  _.each(buckets, function (bucket) {
    const mode = bucket.mode;
    const rulesList = bucket.rules;

    _.each(rulesList, function (rule) {
      const cssStyles = rule.styles;

      // @ts-expect-error - TS2769 - No overload matches this call.
      _.each(cssStyles, function (cssValue, propName: WebflowCssPropOrVar) {
        // HACK:
        //
        // When we encounter `backdrop-filter` but it's not part of our computed
        // styles map, we inject the `browser` entry here and carry on.
        //
        // This prevents missing `backdrop-filter` computed styles from
        // basically erasing what's in our styleblock data (in this context,
        // inside `cssStyles`).
        //
        // We could possibly generalize this behavior for any property that
        // happens to be unsupported by the designer's browser, which could
        // _appear_ less-hacky since it's not a one-off, but to minimize the
        // blast-radius of this hack we're keeping it isolated to
        // `backdrop-filter`, for now.
        //
        // We can safely remove this hack when Firefox ships support for
        // `backdrop-filter`, which can be tracked here:
        //
        // https://bugzilla.mozilla.org/show_bug.cgi?id=1578503
        if (
          propName === 'backdrop-filter' &&
          !allStylesMap[propName] &&
          !browserSupportsBackdropFilter
        ) {
          allStylesMap[propName] = {
            prop: 'backdrop-filter',
            value: {browser: {value: cssValue}},
            from: {},
            path: 'backdropFilters',
            section: 'effects',
          };
        }

        const variableId =
          StyleVariables.getVariableIdFromDefinitionPlaceholder(propName);
        if (variableId != null) {
          propName = `--${StyleVariables.renderCssVariableName(variables, variableCollections, slugMap, variableId)}`;
        }

        const style = allStylesMap[propName];

        if (!style) {
          // TODO(Variable Overrides): Do we need this anymore?
          //
          // CSS custom properties (`--*`) come from `:root` rules and
          // won't be in CSS_PROPERTY_DETAILS — silence those. For other
          // misses, log in dev only. @see STYL-2308.
          if (typeof propName === 'string' && propName.startsWith('--')) {
            return;
          }
          if (process.env.NODE_ENV === 'development') {
            console.error(
              '[%o] Could not find style object for property: "%o"\nLESS: "%o"',
              mode,
              propName,
              cssValue
            );
          }
          return;
        }

        // VERY IMPORTANT: If an upstream rule only contributes typographic styles via inheritance,
        // ignore any non-typographic styles from this rule
        // (e.g. body with a background image should not pass that image to it's children, but it should pass the text color)
        if (mode === UPSTREAM && rule.inherited && !style.typographic) {
          return;
        }

        const isCombo = style.combo;

        // Clean up CSS values to be in Webflow format
        const lessValue = cleanCssValue(propName, cssValue);

        // 1. CssPropertyDetail.value could be undefined
        // @ts-expect-error - TS18048 - 'style.value' is possibly 'undefined'.
        style.value[mode] = toWebflowCssValueObject(
          lessValue,

          // This change forces the style system to skip parsing a value for an
          // expanded property,  because we only want to render raw strings in
          // the UI. If we did not force it skip, it may parse
          // border-spacing: 20px into {value: 20, unit: 'px'} and cause
          // problems, because the UI components are assuming the incoming
          // declaration values are strings etc.
          //
          // We may add parsing later on depending on how the UI evolves. But
          // for now, the MVP is straight up raw strings.
          //
          // @ts-expect-error 👉 2. isCombo needs to be boolean-ified
          isCombo ||
            style.section === Section.EXPANDED_CSS ||
            // HACK: Prevents numeric font-family names from being parsed as numbers,
            // ensuring they remain as strings.
            propName === 'font-family'
        );

        if (mode === UPSTREAM || mode === DOWNSTREAM || mode === DEFAULT) {
          // @ts-expect-error - TS18048 - 'style.from' is possibly 'undefined'.
          style.from[mode] = rule;
        }
      });
    });
  });

  // Collapse background-* to a consolidated 'background' property
  // Collapse transition-* to a consolidated 'transition' property
  const subProps = allStyles.filter((style) => isMultiPartProp(style.prop));

  _.each(subProps, function (subProp: Record<any, any>) {
    const cssProp = subProp.prop;
    const comboProp = strLeft(cssProp, '-'); // e.g. 'background', 'transition'
    const subKey = getSubvalueKey(cssProp); // e.g. 'position', 'repeat', 'property', 'duration', 'timing'
    let masterStyle: any = allStylesMap[comboProp];

    // Mark as a subproperty so we can ignore it later since we are combining
    subProp.sub = true;

    if (!masterStyle) {
      masterStyle = {
        prop: comboProp,
        path: subProp.path,
        multi: true,
        from: {},
        value: {},
      };

      allStyles.push(masterStyle);
      allStylesMap[comboProp] = masterStyle;
    }

    // Consolidate upstream/id/local/downstream values in master property
    _.each(CASCADE_CHAIN, function (mode) {
      const modeFrom = subProp.from[mode];
      if (modeFrom) {
        if (!masterStyle.from[mode]) {
          masterStyle.from[mode] = {};
        }

        // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
        masterStyle.from[mode][subKey] = modeFrom;
      }

      const modeValue = subProp.value[mode];
      if (modeValue) {
        if (!masterStyle.value[mode]) {
          masterStyle.value[mode] = {};
        }

        // @ts-expect-error - TS2538 - Type 'undefined' cannot be used as an index type.
        masterStyle.value[mode][subKey] = modeValue;
      }
    });
  });

  return allStylesMap;
}

const getLockedCondition = matchExpression<string | number | null>({
  Text: identity,
  Number: identity,
  _: constant(null),
});

export const getMessagesForLockedStyles = (
  element: ExpressionElement,
  lockedStyles: LockedStyles
): {[key: string]: string} => {
  return (
    Object.keys(lockedStyles) as Array<keyof WebflowCssProperties>
  ).reduce((accLockedStyles, lockedStyleKey) => {
    const {when: conditions, message} = lockedStyles[lockedStyleKey]!;
    // If condition is of the type {when: true}
    if (typeof conditions === 'boolean') {
      return conditions
        ? assoc(lockedStyleKey)(message)(accLockedStyles)
        : accLockedStyles;
    } else if (typeof conditions === 'object') {
      // If condition is of the type {when: {property: value}},
      // fold over the conditions to make sure all are true
      for (const conditionKey in conditions) {
        const conditionExpr = Expressions.getIn(
          Expressions.getElementData(element),
          [{in: 'Record', at: conditionKey}]
        );
        const condition = conditionExpr
          ? getLockedCondition(conditionExpr)
          : null;

        if (condition !== conditions[conditionKey]) return accLockedStyles;
      }

      return assoc(lockedStyleKey)(message)(accLockedStyles);
    } else {
      // Should never happen
      throw TypeError(
        'LockedStyle condition has an unknown format: ' + typeof conditions
      );
    }
  }, emptyObject);
};

type GetMessageForLockedGridDisplay = (
  child: ExpressionElement
) => Result<string, null>;

const sliderGridMessage =
  'Grid display is disabled to preserve slider functionality. If you need a different display setting for slider content, add a div block and style it instead.';

const hasLegacyEmbedPayload = (child: ExpressionElement): boolean =>
  Expressions.getElementDataAt(
    child,
    [
      {in: 'Record', at: 'embed'},
      {in: 'Record', at: 'type'},
    ],
    'Text',
    ''
  ) !== '';

export const getMessageForLockedGridDisplay: GetMessageForLockedGridDisplay = (
  child
) => {
  if (ElementTypeUtils.isButton(child)) {
    return Err('Grid display has no effect on Button Elements.');
  }

  if (ElementTypeUtils.isTypographic(child)) {
    return Err('Grid display has no effect on typographic elements.');
  }

  if (ElementTypeUtils.isRichText(child)) {
    return Err('Grid display has no effect on typographic elements.');
  }

  if (ElementTypeUtils.isImageElement(child)) {
    return Err('Grid display has no effect on media elements.');
  }

  if (ElementTypeUtils.isBackgroundVideoElement(child)) {
    return Err('Grid display has no effect on media elements.');
  }

  if (ElementTypeUtils.isVideoEmbedElement(child)) {
    return Err('Grid display has no effect on media elements.');
  }

  if (ElementTypeUtils.isEmbed(child) || hasLegacyEmbedPayload(child)) {
    return Err('Grid display has no effect on embed elements.');
  }

  if (
    ElementTypeUtils.isFormLabel(child) ||
    ElementTypeUtils.isFormCheckboxLabel(child) ||
    ElementTypeUtils.isFormRadioLabel(child)
  ) {
    return Err('Grid display has no effect on typographic elements.');
  }

  if (
    ElementTypeUtils.isFormInputElement(child) ||
    ElementTypeUtils.isSearchInput(child) ||
    ElementTypeUtils.isCommerceAddToCartQuantityInput(child)
  ) {
    return Err('Grid display has no effect on input elements.');
  }

  if (
    ElementTypeUtils.isFormButtonElement(child) ||
    ElementTypeUtils.isFormReCaptcha(child) ||
    ElementTypeUtils.isFormFileUploadLabel(child)
  ) {
    return Err('Grid display has no effect on input elements.');
  }

  if (ElementTypeUtils.isFormCheckboxWrapper(child)) {
    return Err(`Grid layout isn't supported for checkboxes.`);
  }

  if (ElementTypeUtils.isFormRadioWrapper(child)) {
    return Err(`Grid layout isn't supported for radio buttons.`);
  }

  if (ElementTypeUtils.isTabsContent(child)) {
    return Err(`Grid layout isn't supported for tabs.`);
  }

  if (ElementTypeUtils.isGridRow(child)) {
    return Err('Grid support within grid rows is coming soon.');
  }

  if (ElementTypeUtils.isNavbarLink(child)) {
    return Err('Grid display has no effect on typographic elements.');
  }

  if (ElementTypeUtils.isNavbarButton(child)) {
    return Err('Grid display has no effect on this element.');
  }

  if (ElementTypeUtils.isNavbarMenu(child)) {
    return Err(`Grid layout isn't supported for navbars.`);
  }

  if (
    ElementTypeUtils.isDropdownWrapper(child) ||
    ElementTypeUtils.isDropdownToggle(child)
  ) {
    return Err('Grid display has no effect on dropdown elements.');
  }

  if (ElementTypeUtils.isDropdownList(child)) {
    return Err(`Grid layout isn't supported for dropdowns.`);
  }

  if (ElementTypeUtils.isDropdownLink(child)) {
    return Err('Grid display has no effect on typographic elements.');
  }

  if (ElementTypeUtils.isIconWidget(child)) {
    return Err('Grid display has no effect on image elements.');
  }

  if (
    ElementTypeUtils.isSliderSlide(child) ||
    ElementTypeUtils.isSliderMask(child) ||
    ElementTypeUtils.isSliderWrapper(child) ||
    ElementTypeUtils.isSliderArrow(child) ||
    ElementTypeUtils.isSliderNav(child)
  ) {
    return Err(sliderGridMessage);
  }

  if (ElementTypeUtils.isLightboxWrapper(child)) {
    return Err('Grid display has no effect on Lightbox elements.');
  }

  if (ElementTypeUtils.isMapWidget(child)) {
    return Err('Grid display has no effect on Map elements.');
  }

  if (
    ElementTypeUtils.isFacebookWidget(child) ||
    ElementTypeUtils.isTwitterWidget(child)
  ) {
    return Err('Grid display has no effect on social media elements.');
  }

  return Ok(null);
};

export const floatClearGridItemMessages = {
  float: {when: true, message: 'Float has no effect on grid children.'},
  clear: {when: true, message: 'Clear has no effect on grid children.'},
} as const;

export const gridItemMessages = {
  ...floatClearGridItemMessages,
  gridItem: {
    when: true,
    message: `You can't apply grid item styles to a Symbol. Try wrapping the Symbol in a div and style the div instead.`,
  },
} as const;

export const getLockedReasonsForGridItems = ({
  parentDisplay,
  element,
  componentMap,
}: {
  parentDisplay: Display;
  element: ExpressionElement;
  componentMap: Component.ComponentMap;
}) => {
  if (parentDisplay !== 'grid') {
    return emptyObject;
  }

  return isComponent(componentMap)(element)
    ? gridItemMessages
    : floatClearGridItemMessages;
};

// Helper function that takes a Map of styles and converts them to CSS string
export const cssPropertiesToText = (stylesMap: StyleMap) => {
  return stylesMap.reduce((cssText, styleValue, styleProp) => {
    cssText += `${dasherize(styleProp)}: ${String(styleValue)}; `;
    return cssText;
  }, '');
};

const getStylesMapParamsRequirements = weakMemo((atom: NormalizedAtomConfig) =>
  Object.keys(atom.params || {})
    .filter((key) => atom.params?.[key]?.type === 'StylesMap')
    .map((key) => ({
      key,
      selector: atom.params?.[key] as ParamsType,
    }))
);

type ParamsRequirement = {
  key: string;
  selector: ParamsType;
};

type ElementsWithStyleMapParam = {
  element: ExpressionElement;
  path: string[];
  paramsRequirement: ParamsRequirement;
};

const collectElementChildrenNeedingStyleMap = (
  plugins: Plugins.PluginsType,
  element: ExpressionElement,
  path: string[] = [],
  results: ElementsWithStyleMapParam[] = []
): ElementsWithStyleMapParam[] => {
  const children = getChildren(element);
  if (!children) return results;

  return children.reduce((memo, child) => {
    const atom = Plugins.getAtom(plugins, child);
    const paramsRequirements = getStylesMapParamsRequirements(atom);

    memo.push(
      ...paramsRequirements.map((paramsRequirement) => ({
        element: child,
        path: path.concat(child.id),
        paramsRequirement,
      }))
    );

    return collectElementChildrenNeedingStyleMap(
      plugins,
      child,
      path.concat(child.id),
      memo
    );
  }, results);
};

const getFilteredElementsAndPaths = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((element: ExpressionElement) =>
    collectElementChildrenNeedingStyleMap(plugins, element, [element.id])
  )
);

const isRuleExactMatch =
  ({
    mediaQuery,
    pseudoState,
  }: {
    mediaQuery: BreakpointID;
    pseudoState: null | PseudoStateKey;
  }) =>
  (rule: StyleRule) =>
    rule.mediaQuery === mediaQuery &&
    (rule.pseudoState || 'noPseudo') === pseudoState;

const styleRuleToPlainObject =
  (
    swatchMap: {
      [id: string]: string;
    },
    paramRequirement: ParamsRequirement,
    variables: StyleVariables.VariableMap,
    variableCollections: StyleVariables.VariableCollectionMap,
    slugMap: Plugins.PluginsType['slugMap']
  ) =>
  (
    accStyleProperties: {
      [key: string]: string;
    },
    styleRule: StyleRule
  ) =>
    paramRequirement.selector.excludeInherited && styleRule.inherited
      ? accStyleProperties
      : Object.keys(styleRule.styles).reduce((result, key) => {
          const camelizedProperty = camelize(key);
          return paramRequirement.selector.filterProperties.includes(
            camelizedProperty
          )
            ? assoc(camelizedProperty)(
                replaceSwatchPlaceholdersForExport(
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Partial<Record<WebflowCssProperty, any>>'.
                  styleRule.styles[key],
                  swatchMap,
                  variables,
                  variableCollections,
                  slugMap
                )
              )(result)
            : result;
        }, accStyleProperties);

const updateMapWithElementAndPath =
  (
    expression: Expression | null,
    styleBlockState: StyleBlockStateType,
    cssVariablesState: CssVariablesStateType,
    swatchMap: {
      [id: string]: string;
    },
    plugins: Plugins.PluginsType,
    componentMap: Component.ComponentMap
  ) =>
  (
    elementToStyleMap: StylesMap,
    {element, path, paramsRequirement}: ElementsWithStyleMapParam
  ): StylesMap => {
    const value = {
      path,
      styles: styleBlockState
        .getOrderedBreakpointIds()
        .reduce((memo, mediaQuery) => {
          const stateStyles = PSEUDO_KEYS.reduce(
            (styleMapForState, pseudoState) => {
              const stylesForState = getLinkedRules({
                pathToElement: path,
                appliedBlocks: getStyleBlockIds(element),
                currentMediaQuery: mediaQuery,
                currentPseudo: pseudoState,
                styleBlockState,
                expression,
                plugins,
                componentMap,
              })
                /**
                 * Since every combination is being iterated over, we filter out non-exact
                 * matches and only store the rules *specific* to this exactly combination
                 * of context. Since every combination is visited, we each set of styles at
                 * least once. This allows to store only the deltas in the DOM and flatten
                 * them via the runtime.
                 */
                .filter(
                  isRuleExactMatch({
                    mediaQuery,
                    pseudoState,
                  })
                )
                .reduce(
                  styleRuleToPlainObject(
                    swatchMap,
                    paramsRequirement,
                    cssVariablesState.variables,
                    cssVariablesState.variableCollections,
                    Plugins.getPluginsSlugMap(plugins)
                  ),
                  emptyObject
                );

              return stylesForState === emptyObject
                ? styleMapForState
                : assoc(pseudoState)(stylesForState)(styleMapForState);
            },
            emptyObject
          );

          return stateStyles === emptyObject
            ? memo
            : assoc(mediaQuery)(stateStyles)(memo);
        }, emptyObject),
    } as const;
    return assoc(element.id)(
      assoc(paramsRequirement.key)(value)(
        elementToStyleMap[element.id] || emptyObject
      )
    )(elementToStyleMap);
  };

/**
 * Helper selector that avoids busting the cache when e.g. parts of
 * `designerState` change that aren't actually relevant for computing the map.
 */
const buildStylesMap_ = createSelector(
  ({plugins}) => plugins,
  ({cssVariablesState}) => cssVariablesState,
  ({designerState}) => designerState.components,
  ({designerState}) => getLiveComponentMap(designerState),
  ({focusedSymbolNativeIdPath}) => focusedSymbolNativeIdPath,
  ({styleBlockState}) => styleBlockState,
  (
    plugins,
    cssVariablesState,
    components,
    liveComponentMap,
    focusedSymbolNativeIdPath,
    styleBlockState
  ) =>
    defer(() => {
      const bodyElement = getExpandedBody(liveComponentMap)(components);

      // Filter elements that haven't "asked for" the styles map. This should always
      // go first as the # of elements that require their StyleMap will be
      // order(s) of magnitude smaller than the total # of elements
      const elementsAndPaths = bodyElement
        ? getFilteredElementsAndPaths(plugins)(bodyElement)
        : [];

      const swatchMap = createSwatchMap(cssVariablesState.swatches);

      const expression = getCurrentComponentRender(
        liveComponentMap,
        focusedSymbolNativeIdPath
      );

      const appliedStyles = elementsAndPaths.reduce(
        updateMapWithElementAndPath(
          expression,
          styleBlockState,
          cssVariablesState,
          swatchMap,
          plugins,
          components
        ),
        emptyObject
      );

      return appliedStyles;
    })
);

export const buildStylesMap = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((cssVariablesState: CssVariablesStateType) =>
    weakMemo((designerState: DesignerStateType) =>
      weakMemo((focusedSymbolNativeIdPath: string[]) =>
        weakMemo(
          (styleBlockState: StyleBlockStateType): Lazy<StylesMap> =>
            buildStylesMap_({
              plugins,
              cssVariablesState,
              designerState,
              focusedSymbolNativeIdPath,
              styleBlockState,
            })
        )
      )
    )
  )
);

/**
 * Variation of the `buildStylesMap` function that requires only arguments that we can reasonable supply to Canvas app.
 */
export const buildStylesMapForCanvasApp = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((cssVariablesState: CssVariablesStateType) =>
      weakMemo((components: DesignerStateType['components']) =>
        weakMemo(
          (styleBlockState: StyleBlockStateType): Lazy<StylesMap> =>
            defer(() => {
              const body = getBody(components);

              const bodyElement = body
                ? Expressions.getElement(expandUserComponents(components)(body))
                : null;
              const styleableElementsAndPaths = bodyElement
                ? // Filter elements that haven't "asked for" the styles map. This should always
                  // go first as the # of elements that require their StyleMap will be
                  // order(s) of magnitude smaller than the total # of elements
                  getFilteredElementsAndPaths(plugins)(bodyElement)
                : [];

              const swatchMap = createSwatchMap(cssVariablesState.swatches);

              // As in places where Canvas App is used user is not able to focus specific component
              // focusedSymbolNativeIdPath is always []
              const focusedSymbolNativeIdPath = [] as string[];
              const expression = getCurrentComponentRender(
                components,
                focusedSymbolNativeIdPath
              );

              const appliedStyles = styleableElementsAndPaths.reduce(
                updateMapWithElementAndPath(
                  expression,
                  styleBlockState,
                  cssVariablesState,
                  swatchMap,
                  plugins,
                  components
                ),
                emptyObject
              );

              return appliedStyles;
            })
        )
      )
    )
);

export const localStylesCssDiff = (
  oldLocalStyle: StyleMap,
  newLocalStyle: StyleMap
): jsdiff.Change => {
  const oldLocalStylesText = cssPropertiesToText(oldLocalStyle);
  const newLocalStylesText = cssPropertiesToText(newLocalStyle);
  // @ts-expect-error - TS2741 - Property 'value' is missing in type 'Change[] & void' but required in type 'Change'.
  return jsdiff.diffCss(oldLocalStylesText, newLocalStylesText, {
    // @ts-expect-error - TS2769 - No overload matches this call.
    ignoreWhiteSpace: false,
    newlineIsToken: false,
  });
};

export const getResetStyle = (
  stylePropertyName: StylePath.StylePanelPropertyName
): StyleUpdate | StyleUpdate[] => {
  const path = StylePath.getPath(stylePropertyName);
  // check if compound path
  if (typeof path === 'object') {
    const paths: Array<StylePath.StyleSystemPath> =
      StylePath.getCompoundPaths(path);
    return paths.map<StyleUpdate>(resetValueUpdate);
  } else {
    return resetValueUpdate(path);
  }
};

export type GetAffectsMapStyleMapResult = [
  (styleBlockId: StyleBlockId) => string[],
  (styleBlockId: StyleBlockId) => string[],
  {
    get: (
      styleBlockId: StyleBlockId
    ) => undefined | ImmutableMap<string, number>;
  },
];

const getComponentRootsByElementId = weakMemo(
  (
    componentMap: Component.ComponentMap
  ): Map<string, ExpressionElement | null> => {
    const rootsByElementId = new Map<string, ExpressionElement | null>();
    componentMap.forEach((component) => {
      const render = Component.getRender(component);
      const root = Expressions.getElement(render);
      for (const id of Object.keys(Expressions.allElements(render))) {
        if (!rootsByElementId.has(id)) rootsByElementId.set(id, root);
      }
    });
    return rootsByElementId;
  }
);

const getComponentRootFromElementId = (
  componentMap: Component.ComponentMap,
  id: string
): ExpressionElement | null =>
  getComponentRootsByElementId(componentMap).get(id) ?? null;

export const getAffectsMapStyleMapForThisPage = weakMemo(
  weakMemo((styleBlockState: StyleBlockStateType) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((plugins: Plugins.PluginsType) =>
        singleMemo((currentPageId: string): GetAffectsMapStyleMapResult => {
          const affectedElementsOnThisPage: Record<StyleBlockId, string[]> = {};
          const affectedComponents: Record<StyleBlockId, string[]> = {};

          const updateAffectedMaps = (styleBlockId: StyleBlockId) => {
            if (affectedElementsOnThisPage[styleBlockId]) return;

            const elementIds = new Set<string>();
            const componentIds = new Set<string>();

            const affectedIds = calculateAffectedForStyleBlock({
              componentMap,
              plugins,
              styleBlockState,
              styleBlockId,
            });

            for (const elementId of affectedIds) {
              const componentRoot = getComponentRootFromElementId(
                componentMap,
                elementId
              );
              if (!componentRoot) continue;

              if (isUserComponentRoot(componentMap, componentRoot)) {
                componentIds.add(componentRoot.id);
              } else {
                elementIds.add(elementId);
              }
            }

            affectedElementsOnThisPage[styleBlockId] = [...elementIds];
            affectedComponents[styleBlockId] = [...componentIds];
          };

          const getAffectedElementsOnThisPage = (
            styleBlockId: StyleBlockId
          ) => {
            updateAffectedMaps(styleBlockId);
            return affectedElementsOnThisPage[styleBlockId] || [];
          };

          const getAffectedComponents = (styleBlockId: StyleBlockId) => {
            updateAffectedMaps(styleBlockId);
            return affectedComponents[styleBlockId] || [];
          };

          const affectsMap = {
            get(styleBlockId: StyleBlockId) {
              const base = styleBlockState.affectsMap.get(styleBlockId);
              if (!base) return base;
              return base.set(
                currentPageId,
                getAffectedElementsOnThisPage(styleBlockId).length
              );
            },
          } as const;

          return [
            getAffectedElementsOnThisPage,
            getAffectedComponents,
            affectsMap,
          ];
        })
      )
    )
  )
);

// Memoized so the focused-component path returns a stable array, like the
// page path does.
const getAffectedNodesInComponent = weakMemo(
  (affectedIds: ReadonlyArray<string>) =>
    weakMemo((render: Expression): string[] => {
      const componentElements = Expressions.allElements(render);
      return affectedIds.filter((id) => Object.hasOwn(componentElements, id));
    })
);

// Elements inside component definitions count toward their component, not
// this page, matching the Style selectors panel. While a component is being
// edited, its own elements are the ones on screen, so count those instead.
export const getAffectedNodesOnThisPage = (
  designerState: DesignerStateType,
  styleBlockState: StyleBlockStateType,
  styleBlockId: StyleBlockId | null | undefined,
  focusedUserComponent: Component.ComponentType | null
): string[] => {
  if (!styleBlockId) return emptyArray;

  const componentMap = getLiveComponentMap(designerState);
  if (focusedUserComponent) {
    return getAffectedNodesInComponent(
      calculateAffectedForStyleBlock({
        componentMap,
        plugins: designerState.plugins,
        styleBlockState,
        styleBlockId,
      })
    )(Component.getRender(focusedUserComponent));
  }

  const [getAffectedElementsOnThisPage] = getAffectsMapStyleMapForThisPage(
    styleBlockState
  )(componentMap)(designerState.plugins)(designerState.currentPageId);
  return getAffectedElementsOnThisPage(styleBlockId);
};
